import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import get from "lodash/get";
import { AccessType, ResponsiveHeroImage, ReviewRating, SocialShare, Link } from "@quintype/components";
import { StoryTags } from "../story-page-components/story-tags.js";
import { RelatedStories } from "../story-page-components/related-stories.js";
import { DfpAd } from "../dfp-ad";
import { MetypeCommentingWidget } from "@metype/components";
import { breakpoint } from "../../../utils/breakpoint";
import { addTargetBlankToExtLinks } from "../../../utils/add-target-blank-to-extlinks";
import { Author } from "../../components/basic/author.js";
import { Separator } from "../../components/basic/separator-line";
import {
  altTagOfHeroImages,
  getSocialShareProps,
  storyColor,
  sectionPath,
  getHorizontalSocialShare,
  isEmpty
} from "../../utils";
import { getAccessData, getStoryCards, handleAccess, checkIfStoryPublic } from "../sso-components/sso-utils";
import { getSocialShareTemplate } from "../story-page-components/social-share/get-social-template";

import "./hero-wide-text.m.css";

function renderMetype(story, metypeConfig = {}, previewStory = false) {
  if (previewStory || isEmpty(metypeConfig)) return null;

  return (
    <div className="blank-story--metype-widget">
      <MetypeCommentingWidget
        host={metypeConfig.host}
        accountId={parseInt(metypeConfig.accountId)}
        pageURL={generateHostUrl(story)}
        primaryColor={metypeConfig.primaryColor}
      />
    </div>
  );
}

function HeroWideTextStoryTemplate(props) {
  const metypeConfig = get(props, ["config", "theme-attributes", "metype-config"], {});
  const textStory = get(props, ["config", "theme-attributes", "text-story"], "default");
  const isReviewStory = props.story["story-template"] === "review";
  const haveFocusPoint = !!get(props, ["story", "hero-image-metadata", "focus-point"]);
  const imageElement = breakpoint("tablet") ? (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={[16, 9]}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640]}
      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  ) : (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={textStory === "hero-wide" ? [8, 3] : [16, 9]}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640, 960, 1200]}
      sizes="(max-width: 500px) 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  );
  const publishedTime = props.story["last-published-at"]
    ? props.story["last-published-at"]
    : props.story["first-published-at"];
  const color = storyColor(props.config, props.story);
  const sectionColor = {
    borderBottomColor: color
  };
  const authorsArray = get(props.story, ["authors"])
    ? get(props.story, ["authors"])
    : [
        {
          name: props.story["author-name"],
          id: props.story["author-id"]
        }
      ];
  const storyAccess = get(props, ["access"]);
  const accessLoading = get(props, ["accessLoading"]);

  const cdnImage = get(props, ["config", "cdn-image"], "");
  const storyCards = get(props, ["story", "cards"], []);
  const isStoryPublic = checkIfStoryPublic(props.story);

  function renderSocialShare() {
    const socialShareProps = getSocialShareProps(props.config, props.story);
    const horizontalSocialShareStyle = getHorizontalSocialShare(props.config);

    return (
      <div
        styleName="story-grid-item social-share"
        className={`story-byline__social-share horizontal-social-share-wrapper`}
      >
        <SocialShare template={getSocialShareTemplate(horizontalSocialShareStyle, false)} {...socialShareProps} />
      </div>
    );
  }

  function getCaption() {
    const caption = get(props, ["story", "hero-image-caption"], "");
    const attribution = get(props, ["story", "hero-image-attribution"], "");
    return !(caption || attribution) ? null : (
      <div styleName="story-grid-item image-caption">
        <span data-testid="hero-wide-caption" dangerouslySetInnerHTML={{ __html: caption }} />
        {caption && attribution ? <Separator fill="#000" /> : null}
        <span data-testid="hero-wide-attribution" dangerouslySetInnerHTML={{ __html: attribution }} />
      </div>
    );
  }

  return (
    <div data-testid="hero-wide-grid" className="text-story-grid">
      {/* hero image */}
      <div data-testid="hero-wide-image" className="image-content-wrapper">
        {props.story["hero-image-s3-key"] ? (
          <figure className="qt-responsive-image" styleName={!haveFocusPoint ? "full-height" : ""}>
            {imageElement}
          </figure>
        ) : (
          <div className="blank-story-hero-image__fallback-placeholder" />
        )}
      </div>

      {/* Caption / Attribute */}
      {getCaption()}

      {/* Section-name */}
      <Link
        data-story-section-name="true"
        className="story-section"
        styleName="story-grid-item"
        href={"/" + sectionPath(props.config, props.story.sections[0])}
        style={sectionColor}
      >
        {props.story.sections[0]["display-name"] || props.story.sections[0]["name"]}
      </Link>

      {/* Headline */}
      <h1 data-testid="hero-wide-headline" styleName="story-grid-item" className="story-headline">
        <bdi dangerouslySetInnerHTML={{ __html: props.story.headline }} />
      </h1>

      {/* Summary   */}
      {props.story.subheadline && (
        <p data-testid="hero-wide-subheadline" styleName="story-grid-item" className="story-summary">
          <bdi dangerouslySetInnerHTML={{ __html: props.story.subheadline }} />
        </p>
      )}

      {/* Author */}
      <div data-testid="hero-wide-author" styleName="story-grid-item" className="story-byline__author_time">
        <Author
          config={props.config}
          authors={authorsArray}
          isLink
          showDateOnly
          date={publishedTime}
          textStory={textStory}
        />
      </div>

      {/* Social-share */}
      {renderSocialShare()}

      {/* Horizontal Line */}
      <div data-testid="hero-wide-horizontal-line" styleName="story-grid-item horizontal-line" />

      {/* ReviewRating */}
      {isReviewStory && (
        <div styleName="story-grid-item" className="rating__wrapper" data-test-id="rating-wrapper">
          <h5 className="rating__title">
            {get(props.story, ["metadata", "review-title"])}{" "}
            <span className="rating__value" data-test-id="rating-value">
              ({get(props.story, ["metadata", "review-rating", "value"], 0)} / 5)
            </span>
          </h5>
          <div className="rating">
            <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />
          </div>
        </div>
      )}

      {/* Story-cards */}
      <div data-testid="hero-wide-cards" styleName="story-grid-item story-cards">
        {isStoryPublic
          ? getStoryCards(storyCards, props.story, cdnImage)
          : handleAccess(props.story, storyAccess, accessLoading, props.config)}
      </div>

      {/* StoryTags */}
      <div data-testid="hero-wide-tags" styleName="story-grid-item story-tags">
        <StoryTags tags={props.story.tags} config={props.config} />
      </div>

      {/* Related Stories */}
      <div data-testid="related-hero-wide" styleName="story-grid-item blank-story__aside">
        <div className="app-ad app-ad--story-mrec">
          <DfpAd adtype="Story-Mrec" />
        </div>
        {(isReviewStory || textStory === "hero-wide") && <RelatedStories story={props.story} />}
      </div>

      {/* Metype + Ad */}
      {!props.preview && (
        <div data-testid="hero-wide-metype" styleName="metype-ad-wrapper">
          <div className="app-ad app-ad--story-horizontal">
            <DfpAd adtype="Story-Bottom-Ad" />
          </div>
          {renderMetype(props.story, metypeConfig, props.preview)}
        </div>
      )}
    </div>
  );
}

HeroWideTextStoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool
};

function generateHostUrl(story = {}) {
  if (global.location) {
    return `${global.location.origin}/${story.slug}`;
  }
}

class HeroWideTextStoryAccessWrapper extends React.Component {
  componentDidMount() {
    this.props.initAccessType();
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }

  render() {
    const storyMetadata = get(this.props, ["story", "metadata"], {});
    return (
      <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
        <div style={{ display: "none" }} className="blank-story-metadata">
          {JSON.stringify(storyMetadata)}
        </div>
        {/* DO NOT DELETE, HACK ON PURPOSE. */}
        <HeroWideTextStoryTemplate {...this.props} />
      </div>
    );
  }
}

HeroWideTextStoryAccessWrapper.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool,
  access: PropTypes.object,
  checkAccess: PropTypes.func,
  initAccessType: PropTypes.func,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func
};

class HeroWideTextStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "");
    const accessTypeKey = get(this.props, ["config", "theme-attributes", "accesstypeKey"]);
    const disableMetering = get(this.props, ["config", "theme-attributes", "disableMetering"]) === "true";
    const isStaging = get(this.props, ["config", "theme-attributes", "accesstypeEnvironment"]) === "staging";
    const enableAccesstype = get(this.props, ["config", "theme-attributes", "enableAccesstype"]) === "true";

    return (
      <AccessType
        enableAccesstype={enableAccesstype}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
        email={email}
        phone={phone}
        disableMetering={disableMetering}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
            <HeroWideTextStoryAccessWrapper
              accessIsLoading={accessIsLoading}
              accessUpdated={accessUpdated}
              initAccessType={initAccessType}
              checkAccess={checkAccess}
              {...this.props}
            />
          </div>
        )}
      </AccessType>
    );
  }
}

const mapStateToProps = state => ({
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const HeroWideTextStory = connect(mapStateToProps)(HeroWideTextStoryBase);
